import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Table, Button, Tag, Tooltip, message } from 'antd';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    EyeOutlined,
    FlagOutlined,
    MailOutlined,
    CalendarOutlined,
    DownloadOutlined,
    FileTextOutlined
} from '@ant-design/icons';
import PreviewCVModal from './PreviewCVModal';

// Helper function to format column titles by splitting words onto separate lines
const formatTitle = (title) => {
    return title.split(' ').map((word, index) => (
        <div key={index} style={{ lineHeight: '1.1' }}>{word}</div>
    ));
};
// const backend_url = process.env.REACT_APP_BACKEND_URL;

// Helper function to determine colors based on score values
const getColor = (value, isText = false) => {
    if (isText) {
        // Color scheme for text
        if (value > 80) return '#006600'; // Dark green
        if (value > 60) return '#C55b1d'; // Orange
        return '#ED2019'; // Red
    } else {
        // Color scheme for backgrounds
        if (value > 80) return '#CCFFCC'; // Light green
        if (value > 60) return '#FFFFCC'; // Light yellow
        return '#FFCCCC'; // Light red
    }
};

const RankingTable = ({
                          data, // Array of candidate data
                          returnedCriteria, // Array of additional criteria names
                          flags, // Object mapping ApplicationID to flag colors
                          cycleColor, // Function to handle flag color changes
                          handleButtonClick, // Function to handle CV preview
                          handleUnifiedPrescreenClick, // Function to handle prescreening
                          handleSummaryClick, // Function to handle summary view
                          prescreenLoading, // Object tracking prescreen loading states
                          getPrescreenButtonColor, // Function to get prescreen button color based on status
                          isMultiSelect, // Boolean for multi-select mode
                          selectedCandidates, // Array of selected candidates
                          setSelectedCandidates, // Function to update selected candidates
                          handleMultiplePrescreen, // Function to handle multiple prescreening
                          ENABLE_MULTISELECT_FEATURE, // Feature flag for multi-select functionality
                          jdFilepath,
                          jdText,
                          backendURL,
                          jobID
                      }) => {

    const [showJDModal, setShowJDModal] = useState(false);
    const [downloadOptions, setDownloadOptions] = useState({
        includeRank: true,
        includeCV: false,
        includeAdditionalCriteria: false
    });
    
    // Add PDF modal state for PDF preview
    const [showPDFModal, setShowPDFModal] = useState(false);
    const [pdfRecord, setPdfRecord] = useState(null);

    // Function to get score reasons for a candidate from the data passed from parent component
    const getScoreReasons = useCallback((applicationID) => {
        const candidateData = data.find(item => item.ApplicationID === applicationID);
        if (candidateData?.ScoreReasons) {
            try {
                return typeof candidateData.ScoreReasons === 'string' 
                    ? JSON.parse(candidateData.ScoreReasons) 
                    : candidateData.ScoreReasons;
            } catch (error) {
                console.error('Error parsing score reasons:', error);
                return { error: 'Failed to parse score reasons' };
            }
        }
        return {};
    }, [data]);

    // Function to handle tooltip visibility change - simplified to avoid infinite loops
    const handleTooltipVisibleChange = useCallback((visible, applicationID) => {
    }, []);

    // Function to render score with tooltip
    const renderScoreWithTooltip = (text, record, fieldName, displayName) => {
        const reasons = getScoreReasons(record.ApplicationID);
        
        console.log(`renderScoreWithTooltip called for ${displayName}:`, {
            applicationID: record.ApplicationID,
            fieldName,
            reasons,
            hasReasons: !!reasons,
            reasonValue: reasons ? reasons[fieldName] : null
        });
        
        let tooltipContent = (
            <div>
                <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#ffffff', marginBottom: '3px' }}>
                    {displayName}: {text}
                </div>
                <div style={{ fontSize: '11px', color: '#f5f5f5' }}>
                    <span>Hover to view details</span>
                </div>
            </div>
        );
        
        if (reasons) {
            if (reasons.error) {
                tooltipContent = (
                    <div>
                        <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#ffffff', marginBottom: '3px' }}>
                            {displayName}: {text}
                        </div>
                        <div style={{ fontSize: '11px', color: '#ff7875' }}>
                            Error: {reasons.error}
                        </div>
                    </div>
                );
            } else {
                const reason = reasons[fieldName];
                console.log(`Looking for reason in field ${fieldName}:`, reason);
                
                if (reason && reason.trim()) {
                    tooltipContent = (
                        <div>
                            <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#ffffff', marginBottom: '3px' }}>
                                {displayName}: {text}
                            </div>
                            <div style={{ fontSize: '11px', color: '#f5f5f5', marginBottom: '1px' }}>
                                <strong>Explanation:</strong>
                            </div>
                            <div style={{ 
                                whiteSpace: 'pre-wrap', 
                                fontSize: '11px',
                                color: '#ffffff',
                                lineHeight: '1.2'
                            }}>
                                {reason}
                            </div>
                        </div>
                    );
                } else {
                    tooltipContent = (
                        <div>
                            <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#ffffff', marginBottom: '3px' }}>
                                {displayName}: {text}
                            </div>
                            <div style={{ fontSize: '11px', color: '#f5f5f5' }}>
                                No explanation available for {fieldName}.
                            </div>
                        </div>
                    );
                }
            }
        } else {
            console.log(`No reasons found for ${record.ApplicationID}`);
        }

        return (
            <Tooltip 
                title={tooltipContent}
                placement="top"
                overlayStyle={{ 
                    maxWidth: '300px',
                    whiteSpace: 'pre-wrap'
                }}
                onVisibleChange={(visible) => {
                    handleTooltipVisibleChange(visible, record.ApplicationID);
                }}
            >
                <div 
                    className="score-cell"
                    style={{
                        color: getColor(text, true),
                        padding: '10px',
                        borderRadius: '7px',
                        fontWeight: 'bold',
                        fontSize: returnedCriteria.length >= 3 ? 14 : 16,
                        cursor: 'pointer',
                        position: 'relative'
                    }}
                >
                    {text}
                </div>
            </Tooltip>
        );
    };

    useEffect(() => {
        if (!jobID) {
            console.warn('RankingTable: jobID prop is missing');
        }
        if (!jdText && !jdFilepath) {
            console.warn('RankingTable: Both jdText and jdFilepath are missing');
        }
        if (!backendURL) {
            console.warn('RankingTable: backendURL prop is missing');
        }
        }, [jobID, jdText, jdFilepath, backendURL]);

    useEffect(() => {
        if (showJDModal) {
            const handleBodyClick = (e) => {
                if (!e.target.closest('[data-modal-content]')) {
                    setShowJDModal(false);
                }
            };
            
            const timer = setTimeout(() => {
                document.body.addEventListener('click', handleBodyClick);
            }, 100);
            
            return () => {
                clearTimeout(timer);
                document.body.removeEventListener('click', handleBodyClick);
            };
        }
    }, [showJDModal]);



    const handleBulkDownload = async () => {
        console.log('Current selectedCandidates:', selectedCandidates);
        if (selectedCandidates.length === 0){
            message.warning('Please select the candidates');
            return;
        }
        try{
            const sessionID = getSessionID(); // Get session ID
            if (!sessionID) {
                message.error('No session found. Please log in again.');
                return;
            }
            message.loading(`Preparing Reports...`);
            const response = await fetch(`${backendURL}/v1/download-report`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    candidates: selectedCandidates,
                    jobTitle: 'CV Ranking Results',
                    returnedCriteria: [],
                    includeRank: downloadOptions.includeRank,
                    includeCV: downloadOptions.includeCV,
                    includeAdditionalCriteria: downloadOptions.includeAdditionalCriteria,
                    jobID: jobID,  // Need to pass jobID for CV retrieval
                    userID: sessionID  // Need to pass userID for CV retrieval
                })
            });
            if (!response.ok){
                throw new Error('Download unsuccessful');
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `bulk_candidate_reports_${new Date().toISOString().split('T')[0]}.zip`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            message.success(`Downloaded ${selectedCandidates.length} reports`);

        } catch (error){
            console.error('Download error', error);
            message.error('Download error, please retry');
        }
    };

    // CSV download function for table data
    const handleDownloadTableCSV = () => {
        if (!data || data.length === 0) {
            message.warning('No data available to download');
            return;
        }

        try {
            // Prepare CSV data
            const csvData = prepareCSVData(data, returnedCriteria);
            
            // Create and download CSV file
            downloadCSV(csvData, 'Candidate_Ranking_Table.csv');
            
            message.success('CSV downloaded successfully');
        } catch (error) {
            console.error('CSV download error:', error);
            message.error('Failed to download CSV');
        }
    };

    // Helper function to prepare CSV data
    const prepareCSVData = (tableData, criteria) => {
        // Define CSV column headers
        const headers = [
            'Rank',
            'Weighted Score',
            'Name',
            'General Impression',
            'Functional Skillset',
            'Industry Experience',
            'Education',
            'Career Progression',
            'Collaborative Skills',
            'Writing',
            'Role Longevity'
        ];

        // Add dynamic criteria columns
        if (criteria && criteria.length > 0) {
            criteria.forEach(criterion => {
                if (criterion.trim()) {
                    headers.push(criterion);
                }
            });
        }

        // Prepare CSV row data
        const rows = tableData.map((candidate, index) => {
            const row = [
                index + 1, 
                candidate.Weighted_Score || candidate.WeightedScore || "False",
                candidate.Name || candidate.ApplicantFirstName || "False",
                candidate.Overall || candidate.GeneralImpression || candidate.General_Impression || "False",
                candidate.TechnicalSkills || candidate.Technical_Skillset || "False",
                candidate.IndustryExperience || candidate.Industry_Experience || "False",
                candidate.Education || "False",
                candidate.CareerProgression || candidate.Career_Progression || "False",
                candidate.Communication || "False",
                candidate.Writing || "False",
                candidate.RoleLongevity || candidate.Longevity || "False"
            ];

        // Add dynamic criteria data
        if (criteria && criteria.length > 0) {
            criteria.forEach((criterion, criteriaIndex) => {
                if (criterion.trim()) {
                    // Use the correct field name: Criteria1, Criteria2, Criteria3
                    const fieldName = `Criteria${criteriaIndex + 1}`;
                    const value = candidate[fieldName];
                    row.push(value || 'false');
                }
            });
        }

            return row;
        });
        return [headers, ...rows];
    };

    // Helper function to download CSV file
    const downloadCSV = (csvData, filename) => {
        // Convert data to CSV format
        const csvContent = csvData.map(row => 
            row.map(cell => {
                // Handle cells containing commas, quotes, or newlines
                if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"') || cell.includes('\n'))) {
                    return `"${cell.replace(/"/g, '""')}"`;
                }
                return cell;
            }).join(',')
        ).join('\n');

        // Add BOM to ensure Excel correctly recognizes UTF-8 encoding
        const BOM = '\uFEFF';
        const csvContentWithBOM = BOM + csvContent;

        // Create Blob and download
        const blob = new Blob([csvContentWithBOM], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const handleViewJD = () => {
        //console.log('=== handleViewJD Debug ===');
        //console.log('jdText:', jdText);
        //console.log('jdText type:', typeof jdText);
        //console.log('jdText length:', jdText ? jdText.length : 0);
        //console.log('jdText trimmed:', jdText ? jdText.trim() : '');
        //console.log('jobID:', jobID);
        //console.log('jdFilepath:', jdFilepath);
        //console.log('jdFilepath type:', typeof jdFilepath);
        
        if (jdFilepath && jdFilepath !== '' && jdFilepath !== 'uploaded') {
            console.log('File-based JD detected');
            const fileExtension = jdFilepath.split('.').pop()?.toLowerCase();
            console.log('File extension:', fileExtension);       
            // PDF show and download
            if (fileExtension === 'pdf') {
                console.log('PDF file detected - showing preview');
                if (jobID && jobID !== '') {
                    console.log('Calling displayPDF with jobID:', jobID);
                    displayPDF(jobID);
                } else {
                    console.error('No jobID available for PDF preview');
                    message.error('No jobID available for PDF preview');
                }
            } else {
                // word - download original file
                console.log('DOC/DOCX/TXT file detected - download only');
                if (jobID && jobID !== '') {  
                    console.log('Downloading JD file with jobID:', jobID);
                    downloadJD(jobID);  
                } else {
                    console.error('No jobID available for download');
                    console.log('jobID is empty - check props');
                }
            }
        } else if (jdText && jdText.trim()) {
            // Manual JD - show text modal (only if no file path)
            console.log('Manual JD - show text modal');
            setShowJDModal(true);
        } else {
            console.log('No JD available - neither jdText nor jdFilepath');
            message.warning("NO JD file available");
        }
    };

    // Common function to fetch JD file
    const fetchJDFile = async (jobID) => {
        const sessionID = getSessionID();
        if (!sessionID) {
            throw new Error('No session ID found');
        }
        
        if (!jobID) {
            throw new Error('No job ID available');
        }
        
        const requestURL = `${backendURL}/v1/jd-download?sessionID=${sessionID}&jobID=${jobID}`;
        const response = await fetch(requestURL);
        
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        return response;
    };

    // Function to display PDF using existing PreviewCVModal component
    const displayPDF = async (jobID) => {
        try {
            console.log('=== displayPDF Debug ===');
            console.log('jobID:', jobID);
            
            const response = await fetchJDFile(jobID);
            
            console.log('Response headers:', response.headers);
            console.log('Content-Type:', response.headers.get('content-type'));
            
            // Create blob URL for PDF display
            const blob = await response.blob();
            console.log('Blob created:', blob);
            console.log('Blob type:', blob.type);
            console.log('Blob size:', blob.size);
            
            const blobURL = URL.createObjectURL(blob);
            console.log('Blob URL created:', blobURL);
            
            // Set PDF record data matching PreviewCVModal expected format
            const pdfData = {
                ApplicationID: jobID,
                cvContent: blobURL,
                cvType: 'application/pdf',
                JobID: jobID
            };
            console.log('Setting pdfRecord:', pdfData);
            setPdfRecord(pdfData);
            
            // Show PDF modal
            console.log('Setting showPDFModal to true');
            setShowPDFModal(true);
            
            console.log('=== displayPDF completed ===');
            
        } catch (error) {
            console.error('Error displaying PDF:', error);
            message.error('Failed to display PDF');
            // Fallback to download if preview fails
            downloadJD(jobID);
        }
    };

    // Download JD file function
    const downloadJD = async (jobID) => {
        try {
            console.log('DownloadJD Debug');
            console.log('jobID:', jobID);
            console.log('backendURL:', backendURL);
            
            message.loading('Downloading JD file...');
            
            const response = await fetchJDFile(jobID);
            
            // File name 
            const contentDisposition = response.headers.get('content-disposition');
            let filename = 'job_description';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }
            
            // Blob
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            message.success('JD downloaded successfully');
        } catch (error) {
            // console.error('JD download error:', error);
            message.info("This is a manually entered JD. No file to download.");
        }
    };
    const getSessionID = () => {
        // Get the sessionID from cookie
        const cookies = document.cookie.split(';');
        for (let cookie of cookies) {
            const [name, value] = cookie.trim().split('=');
            if (name === 'userSession') {
                return value;
            }
        }
        return '';
    };

    const handleModalBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            setShowJDModal(false);
        }
    };

    const handleModalContentClick = (e) => {
        e.stopPropagation();
    };

    const tableColumns = useMemo(() => {
        // Column width and styling constants
        const numericColWidth = '7.5%';
        const numericFontSize = returnedCriteria.length >= 3 ? 14 : 16;
        const criteriaColWidth = `${10 / returnedCriteria.length}%`;
        const actionButtonWidth = "5%";

        // Define the standard numerical columns that show candidate scores
        const scoreColumns = [
            {
                title: 'Rank',
                dataIndex: 'Rank',
                key: 'Rank',
                width: '5%',
                align: 'center',
                sorter: (a, b) => a.Rank - b.Rank,
                defaultSortOrder: 'ascend',
                onHeaderCell: () => ({ className: 'custom-table-header' })
            },
            {
                title: formatTitle('Weighted Score'),
                dataIndex: 'Weighted_Score',
                key: 'Weighted_Score',
                width: '7.5%',
                sorter: (a, b) => a.Weighted_Score - b.Weighted_Score,
                render: (text, record) => {
                    const formattedScore = parseFloat(text).toFixed(1);
                    const colorText = getColor(record.Weighted_Score, true);
                    
                    return (
                        <div style={{
                            color: colorText,
                            padding: '0px',
                            borderRadius: '2px',
                            fontWeight: 'bold',
                            fontSize: 15
                        }}>
                            {formattedScore}
                        </div>
                    );
                },
                onHeaderCell: () => ({ className: 'custom-table-header' })
            },
            {
                title: 'Name',
                dataIndex: 'Name',
                key: 'Name',
                align: 'center',
                width: 100,
                sorter: (a, b) => a.Name.localeCompare(b.Name),
                onHeaderCell: () => ({ className: 'custom-table-header' })
            },
            {
                title: formatTitle('General Impression'),
                dataIndex: 'Overall',
                key: 'Overall',
                width: numericColWidth,
                sorter: (a, b) => a.Overall - b.Overall,
                render: (text, record) => renderScoreWithTooltip(text, record, 'Overall', 'General Impression'),
                onHeaderCell: () => ({ className: 'custom-table-header' })
            },
            // Technical Skills column
            {
                title: formatTitle('Functional Skillset'),
                dataIndex: 'Technical_Skillset',
                key: 'Technical_Skillset',
                width: numericColWidth,
                sorter: (a, b) => a.Technical_Skillset - b.Technical_Skillset,
                render: (text, record) => renderScoreWithTooltip(text, record, 'Technical_Skillset', 'Functional Skillset'),
                onHeaderCell: () => ({ className: 'custom-table-header' })
            },
            // Industry Experience column
            {
                title: formatTitle('Industry Experience'),
                dataIndex: 'Industry_Experience',
                key: 'Industry_Experience',
                width: numericColWidth,
                sorter: (a, b) => a.Industry_Experience - b.Industry_Experience,
                render: (text, record) => renderScoreWithTooltip(text, record, 'Industry_Experience', 'Industry Experience'),
                onHeaderCell: () => ({ className: 'custom-table-header' })
            },
            // Education column
            {
                title: 'Education',
                dataIndex: 'Education',
                key: 'Education',
                width: numericColWidth,
                sorter: (a, b) => a.Education - b.Education,
                render: (text, record) => renderScoreWithTooltip(text, record, 'Education', 'Education'),
                onHeaderCell: () => ({ className: 'custom-table-header' })
            },
            // Career Progression column
            {
                title: formatTitle('Career Progression'),
                dataIndex: 'Career_Progression',
                key: 'Career_Progression',
                width: numericColWidth,
                sorter: (a, b) => a.Career_Progression - b.Career_Progression,
                render: (text, record) => renderScoreWithTooltip(text, record, 'Career_Progression', 'Career Progression'),
                onHeaderCell: () => ({ className: 'custom-table-header' })
            },
            // Collaborative Skills column
            {
                title: <div>Collaborative <br /> Skills</div>,
                dataIndex: 'Communication',
                key: 'Communication',
                width: numericColWidth,
                sorter: (a, b) => a.Communication - b.Communication,
                render: (text, record) => renderScoreWithTooltip(text, record, 'Communication', 'Collaborative Skills'),
                onHeaderCell: () => ({ className: 'custom-table-header' })
            },
            // Writing column
            {
                title: 'Grammatical Skills',
                dataIndex: 'Writing',
                key: 'Writing',
                width: numericColWidth,
                sorter: (a, b) => a.Writing - b.Writing,
                render: (text, record) => renderScoreWithTooltip(text, record, 'Writing', 'Grammatical Skills'),
                onHeaderCell: () => ({ className: 'custom-table-header' })
            },
            // Role Longevity column
            {
                title: formatTitle('Role Longevity'),
                dataIndex: 'Longevity',
                key: 'Longevity',
                width: numericColWidth,
                sorter: (a, b) => a.Longevity - b.Longevity,
                render: (text, record) => renderScoreWithTooltip(text, record, 'Longevity', 'Role Longevity'),
                onHeaderCell: () => ({ className: 'custom-table-header' })
            }
        ];

        // Define the action columns (email, prescreen, flags, etc.)
        const actionColumns = [
            // Email actions column
            {
                title: '',
                key: 'actions',
                width: actionButtonWidth,
                fixed: 'left',
                render: (text, record) => (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 0'
                    }}>
                        <Tooltip title="Send acceptance email">
                            <a href={`mailto:${record.Email}?subject=Application Successful&body=${record.AcceptanceBody}`}>
                                <CheckCircleOutlined style={{ color: '#006600', fontSize: '16px' }} />
                            </a>
                        </Tooltip>
                        <Tooltip title="Send rejection email">
                            <a href={`mailto:${record.Email}?subject=Update to your application&body=${record.RejectionBody}`}>
                                <CloseCircleOutlined style={{ color: '#ED2019', fontSize: '16px' }} />
                            </a>
                        </Tooltip>
                        <Tooltip title="Email Candidate">
                            <a href={`mailto:${record.Email}`}>
                                <MailOutlined style={{ fontSize: '16px' }} />
                            </a>
                        </Tooltip>
                    </div>
                ),
                onHeaderCell: () => ({ className: 'empty-table-header' })
            },
            // Prescreen actions column
            {
                title: '',
                key: 'prescreen',
                width: actionButtonWidth,
                fixed: 'left',
                render: (_, record) => (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 0'
                    }}>
                        <Tooltip title={`Prescreening for ${record.Name}`}>
                            <Button
                                icon={<CalendarOutlined style={{ color: '#333333' }} />}
                                onClick={() => handleUnifiedPrescreenClick(record)}
                                loading={prescreenLoading[record.ApplicationID]}
                                style={{
                                    minWidth: '32px',
                                    height: '32px',
                                    padding: '0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '1px solid #d9d9d9', // Always keep gray border
                                    ...(getPrescreenButtonColor(record.ApplicationID) !== '#d9d9d9' && {
                                        backgroundColor: getPrescreenButtonColor(record.ApplicationID),
                                        borderColor: '#d9d9d9' // Keep gray border even when colored
                                    })
                                }}
                            />
                        </Tooltip>
                        <Tooltip title={`View candidate summary for ${record.Name}`}>
                            <Button
                                icon={<FileTextOutlined />}
                                onClick={() => handleSummaryClick(record)}
                                style={{
                                    minWidth: '32px',
                                    height: '32px',
                                    padding: '0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            />
                        </Tooltip>
                    </div>
                ),
                onHeaderCell: () => ({ className: 'empty-table-header' })
            },
            // Flag and CV preview column
            {
                title: '',
                key: 'action',
                width: actionButtonWidth,
                fixed: 'left',
                sorter: (a, b) => {
                    const colorValues = {
                        '#237804': 3,
                        '#d4b106': 2,
                        '#a8071a': 1,
                        '': 0,
                    };
                    return colorValues[flags[a.ApplicationID] || ''] - colorValues[flags[b.ApplicationID] || ''];
                },
                render: (_, record) => (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 0'
                    }}>
                        <Tooltip title="Use this category as you wish">
                            <Tag
                                color={flags[record.ApplicationID] || 'default'}
                                onClick={() => cycleColor(record)}
                                style={{
                                    cursor: 'pointer',
                                    minWidth: '32px',
                                    height: '32px',
                                    margin: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <FlagOutlined />
                            </Tag>
                        </Tooltip>
                        <Tooltip title={`Preview CV for ${record.Name}`}>
                            <Button
                                icon={<EyeOutlined />}
                                onClick={() => handleButtonClick(record)}
                                style={{
                                    minWidth: '32px',
                                    height: '32px',
                                    padding: '0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            />
                        </Tooltip>
                    </div>
                ),
                onHeaderCell: () => ({ className: 'empty-table-header' })
            }
        ];

        // Generate columns for additional criteria
        const criteriaColumns = returnedCriteria
            .filter(criteria => criteria !== "")
            .map((criteria, index) => ({
                title: criteria.length > 25 ? criteria.substring(0, 25) + ".."  : criteria,
                dataIndex: `Criteria${index + 1}`,
                key: `Criteria${index + 1}`,
                width: criteriaColWidth,
                sorter: (a, b, sortOrder) => {
                    const aValue = a[`Criteria${index + 1}`] === "true" ? 1 : a[`Criteria${index + 1}`] === "false" ? 0 : -1;
                    const bValue = b[`Criteria${index + 1}`] === "true" ? 1 : b[`Criteria${index + 1}`] === "false" ? 0 : -1;
                    return sortOrder === 'ascend' ? aValue - bValue : bValue - aValue;
                },
                render: text => {
                    if (text === "true") {
                        return <span style={{ color: '#006600' }}><b>&#10003;</b></span>; // Green checkmark
                    } else if (text === "false") {
                        return <span style={{ color: '#ED2019' }}><b>&#10007;</b></span>; // Red cross
                    }
                    return "";
                },
                onHeaderCell: () => ({
                    className: 'custom-table-header',
                })
            }));

        // Combine all column types in the correct order
        return [...actionColumns, ...scoreColumns, ...criteriaColumns];
    }, [returnedCriteria, flags, prescreenLoading, data]); // Dependencies for useMemo

    return (
        <div style={{ width: '1095px' }}>
            {/* Always visible buttons */}
            <div style={{
                marginBottom: '20px',
                display: 'flex',
                gap: 5,
                justifyContent: 'flex-start',
                alignItems: 'center'
            }}>
                <Button
                    onClick={handleDownloadTableCSV}
                    icon={<FileTextOutlined />}
                    type="primary"
                    style={{ marginBottom: '10px', backgroundColor: '#0A7CD3' }}
                >
                    Download Table as CSV
                </Button>

                <Button
                    onClick={handleViewJD}
                    icon={<FileTextOutlined />}
                    type="primary"
                    style={{ marginBottom: '10px', backgroundColor: '#28a745', marginLeft: '20px' }}
                >
                    View JD
                </Button>
            </div>

            {/* Multi-select dependent buttons */}
            {ENABLE_MULTISELECT_FEATURE && (
                <div style={{
                    marginBottom: '20px',
                    display: 'flex',
                    gap: 5,
                    justifyContent: 'flex-start',
                    alignItems: 'center'
                }}>
                    {isMultiSelect && selectedCandidates.length > 0 && (
                    <>
                        {/* Download Options */}
                        <div style={{
                                display: 'flex',
                                gap: '15px',
                                alignItems: 'center',
                                padding: '8px 12px',
                                backgroundColor: '#f5f5f5',
                                borderRadius: '4px',
                                marginRight: '10px'
                            }}>
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={downloadOptions.includeRank}
                                        onChange={(e) => setDownloadOptions({
                                            ...downloadOptions,
                                            includeRank: e.target.checked
                                        })}
                                        style={{ marginRight: '5px' }}
                                    />
                                    <span style={{ fontSize: '14px' }}>Include Rank</span>
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={downloadOptions.includeCV}
                                        onChange={(e) => setDownloadOptions({
                                            ...downloadOptions,
                                            includeCV: e.target.checked
                                        })}
                                        style={{ marginRight: '5px' }}
                                    />
                                    <span style={{ fontSize: '14px' }}>Include CV Files</span>
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={downloadOptions.includeAdditionalCriteria}
                                        onChange={(e) => setDownloadOptions({
                                            ...downloadOptions,
                                            includeAdditionalCriteria: e.target.checked
                                        })}
                                        style={{ marginRight: '5px' }}
                                    />
                                    <span style={{ fontSize: '14px' }}>Include Additional Criteria</span>
                                </label>
                            </div>
                        <Button
                            onClick={handleMultiplePrescreen}
                            icon={<CalendarOutlined />}
                            type="primary"
                            style={{ marginBottom: '10px', backgroundColor: '#0A7CD3' }}
                        >
                            Prescreen Selected Candidates ({selectedCandidates.length})
                        </Button>

                            <Button
                                onClick={handleMultiplePrescreen}
                                icon={<CalendarOutlined />}
                                type="primary"
                                style={{ marginBottom: '10px', backgroundColor: '#0A7CD3' }}
                            >
                                Prescreen Selected Candidates ({selectedCandidates.length})
                            </Button>

                            <Button
                                onClick={handleBulkDownload}
                                icon={<DownloadOutlined />}
                                type="primary"
                                style={{ marginBottom: '10px', backgroundColor: '#0A7CD3', marginLeft: '20px' }}
                            >
                                Download Selected Candidates Reports({selectedCandidates.length})
                            </Button>
                        </>
                    )}
                </div>
            )}

            {/* Main data table */}
            <Table
                dataSource={data}
                columns={tableColumns}
                pagination={false}
                scroll={{ x: '100%', y: 'calc(100vh - 300px)' }}
                sticky={true}
                rowKey={(record) => record.ApplicationID}
                onRow={(record) => ({
                    onClick: (e) => {
                        // Don't trigger selection when clicking buttons or links
                        if (e.target.closest('button') || e.target.closest('a') || e.target.closest('.ant-tag')) {
                            return;
                        }
                        // Handle row selection for multi-select mode
                        if (ENABLE_MULTISELECT_FEATURE && isMultiSelect) {
                            const isSelected = selectedCandidates.some(
                                selected => selected.ApplicationID === record.ApplicationID
                            );
                            if (isSelected) {
                                setSelectedCandidates(selectedCandidates.filter(
                                    candidate => candidate.ApplicationID !== record.ApplicationID
                                ));
                            } else {
                                setSelectedCandidates([...selectedCandidates, record]);
                            }
                        }
                    },
                    style: {
                        cursor: (ENABLE_MULTISELECT_FEATURE && isMultiSelect) ? 'pointer' : 'default',
                        //backgroundColor: (ENABLE_MULTISELECT_FEATURE && isMultiSelect) && selectedCandidates.some(
                          //  selected => selected.ApplicationID === record.ApplicationID
                        //) ? '#e6f7ff' : '#fafafa'
                    }
                })}
                rowClassName={(record) => {
                    return (ENABLE_MULTISELECT_FEATURE && isMultiSelect) && selectedCandidates.some(
                        selected => selected.ApplicationID === record.ApplicationID
                    ) ? 'ant-table-row-selected' : '';
                }}
            />

            {/* Styles for selected rows */}
            <style jsx global>{`
                .ant-table-header {
                    position: sticky !important;
                    top: 0;
                    z-index: 1000 !important;
                    background: white;
                }
                .ant-table-header th {
                    background: #fafafa !important;
                }
                .ant-table-row-selected > td {
                    background-color: #e6f7ff !important;
                }
                .ant-table-row-selected:hover > td {
                    background-color: #bae7ff !important;
                }
                .ant-table-tbody > tr:not(.ant-table-row-selected) > td {
                    background: #fafafa !important;
                }
            `}</style>

            {/* Styles for selected rows */}
            <style jsx global>{`
                .ant-table-header {
                    position: sticky !important;
                    top: 0;
                    z-index: 2;
                    background: white;
                }
                .ant-table-header th {
                    background: #fafafa !important;
                }
                .ant-table-row-selected > td {
                    background-color: #e6f7ff !important;
                }
                .ant-table-row-selected:hover > td {
                    background-color: #bae7ff !important;
                }
                .ant-table-tbody > tr:not(.ant-table-row-selected) > td {
                    background: #fafafa !important;
                }
            `}</style>

            {/* JD Modal */}
            {showJDModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    zIndex: 9999,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}
                onClick={handleModalBackdropClick} 
                >
                    
                    <div 
                        data-modal-content
                        style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.75)',
                            borderRadius: '8px',
                            padding: '24px',
                            maxWidth: '80%',
                            maxHeight: '80%',
                            overflow: 'auto',
                            backdropFilter: 'blur(10px)',
                            position: 'relative',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                        }}
                        onClick={handleModalContentClick}
                    >
                        <Button
                            onClick={() => setShowJDModal(false)}
                            style={{
                                position: 'absolute',
                                top: '16px',
                                right: '16px',
                                minWidth: 'auto',
                                padding: '8px'
                            }}
                            icon={<CloseCircleOutlined />}
                        />
                        
                        <h2 style={{ marginBottom: '20px', color: '#333' }}>
                            Job Description
                        </h2>
                        
                        <div style={{
                            whiteSpace: 'pre-wrap',
                            lineHeight: '1.6',
                            color: '#333',
                            fontFamily: 'monospace',
                            fontSize: '14px'
                        }}>
                            {jdText}
                        </div>
                        {/* Download JD button */}
                        <div style={{ 
                            textAlign: 'center', 
                            marginTop: '20px',
                            paddingTop: '20px',
                            borderTop: '1px solid #eee'
                        }}>
                            <Button
                                onClick={() => {
                                    if (jobID) {
                                        console.log('Downloading JD file with jobID:', jobID);
                                        downloadJD(jobID);
                                    } else {
                                        message.warning('No JD file available for download');
                                    }
                                }}
                                type="primary"
                                icon={<DownloadOutlined />}
                                style={{
                                    padding: '8px 16px',
                                    height: 'auto',
                                    backgroundColor: '#1890ff',
                                    borderRadius: '8px',
                                    border: 'none',  
                                    boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                                }}
                                disabled={!jobID}
                            >
                                Download JD
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* PDF Modal using existing PreviewCVModal component */}
            <PreviewCVModal
                open={showPDFModal}
                setOpen={setShowPDFModal}
                record={pdfRecord}
                backendURL={backendURL}
                getCookie={getSessionID}
                title="Job Description"
                showAnonymizeButton={false}
            />
        </div>
    );
};
export default RankingTable;